<!-- src/components/LazyCascader/index.vue -->
<template>
  <el-cascader
      ref="cascaderRef"
      v-model="currentValue"
      :options="options"
      :props="mergedProps"
      :clearable="clearable"
      :filterable="false"
      :placeholder="placeholder"
      :separator="separator"
      :disabled="disabled"
      :size="size"
      :style="customStyle"
      :class="customClass"
      :show-all-levels="false"
      @change="handleChange"
      @expand-change="handleExpandChange"
  />
</template>

<script>
import { getBirthLocalData} from "@/api/job/postInfo";
/**
 * 懒加载级联选择器组件（完善版）
 *
 * @description
 * - 支持 id/parentId 结构的数据
 * - 动态判断叶子节点（无子数据时可选）
 * - 支持 1-3 层级的树形数据
 * - 自动缓存避免重复请求
 * - 支持按名称搜索（只搜索最后一层）
 * - 最后一层只需点击一次即可选中
 *
 * @example
 * <LazyCascader
 *   v-model="form.region"
 *   :api="getRegionData"
 *   placeholder="请选择地区"
 * />
 */
export default {
  name: 'LazyCascader',

  props: {
    // ========== 基础属性 ==========

    /** v-model 绑定值 */
    value: {
      type: [Array, String],
      default: () => []
    },

    /** 占位符 */
    placeholder: {
      type: String,
      default: '请选择'
    },

    /** 是否可清空 */
    clearable: {
      type: Boolean,
      default: true
    },

    /** 是否禁用 */
    disabled: {
      type: Boolean,
      default: false
    },

    /** 尺寸 */
    size: {
      type: String,
      default: 'small'
    },

    /** 分隔符 */
    separator: {
      type: String,
      default: '/'
    },

    /** 自定义样式 */
    customStyle: {
      type: [String, Object],
      default: ''
    },

    /** 自定义类名 */
    customClass: {
      type: String,
      default: ''
    },

    /** 是否禁用缓存 */
    disableCache: {
      type: Boolean,
      default: false
    },

    /** 最大层级数（超过此层级自动标记为叶子） */
    maxLevel: {
      type: Number,
      default: 3
    },

    /** 是否显示完整的路径标签 */
    showAllLevels: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      // 级联选择器的选项数据
      options: [],

      // 内部绑定的值
      currentValue: [],

      // 数据缓存：parentId -> children[]
      dataCache: new Map(),

      // 正在进行的请求：parentId -> Promise
      pendingRequests: new Map(),

      // 根级节点的父级ID标识
      rootParentId: "0",

      // 回显路径
      echoPath: [],

      // 标记是否正在回显
      isEchoing: false,

      // 记录已检查过的叶子节点
      checkedLeafNodes: new Set(),

      // 搜索结果临时存储
      searchResults: [],

      // 搜索加载状态
      isSearching: false
    }
  },

  computed: {
    /**
     * 合并后的级联配置
     */
    mergedProps() {
      return {
        label: 'name',
        value: 'code',
        children: 'children',
        leaf: 'leaf',
        lazy: true,
        lazyLoad: this.lazyLoad,
        // ========== 关键：只允许选择叶子节点 ==========
        checkStrictly: false,
        // ========== 自定义搜索方法：只搜索叶子节点 ==========
        filterMethod: this.filterMethod
      }
    }
  },

  watch: {
    value: {
      handler(val) {
        this.setValue(val)
      },
      immediate: true
    }
  },

  async created() {
    await this.loadRootData()

    // 如果有初始值，执行回显
    if (this.value && this.hasValue(this.value)) {
      await this.echoValue(this.value)
    }
  },

  methods: {
    // ========== 核心方法 ==========

    /**
     * 判断是否有有效值
     */
    hasValue(value) {
      if (!value) return false
      if (Array.isArray(value)) return value.length > 0
      if (typeof value === 'string') return value.trim().length > 0
      return false
    },

    /**
     * 设置值
     */
    setValue(value) {
      const parsed = this.parsePath(value)
      this.currentValue = parsed || []
    },

    /**
     * 解析路径
     */
    parsePath(value) {
      if (!value) return []
      if (Array.isArray(value)) return value
      if (typeof value === 'string') {
        return value.split(',').map(v => v.trim()).filter(v => v)
      }
      return []
    },

    /**
     * 加载根级数据
     */
    async loadRootData() {
      try {
        const data = await this.fetchChildren(this.rootParentId)
        this.options = data.map(item => this.createNode(item, 0))
      } catch (error) {
      }
    },

    /**
     * 获取子级数据（带缓存）
     * @param {string} parentId - 父级ID
     */
    async fetchChildren(parentId) {
      // ========== 修复1：校验 parentId ==========
      if (!parentId) {
        console.warn('parentId 为空，跳过请求')
        return []
      }
      // 检查缓存
      if (!this.disableCache && this.dataCache.has(parentId)) {
        return this.dataCache.get(parentId)
      }
      // 检查正在进行的请求（返回已有 Promise）
      if (this.pendingRequests.has(parentId)) {
        return this.pendingRequests.get(parentId)
      }
      // ========== 修复2：使用唯一请求 ID 追踪 ==========
      const requestId = `${parentId}_${Date.now()}`

      // 创建新请求
      const request = (async () => {
        try {
          const result = await getBirthLocalData({ parentId: parentId });
          const data = result.data || result || []
          // ========== 修复3：添加数据校验 ==========
          if (!this.disableCache && parentId) {
            this.dataCache.set(parentId, data)
          }
          return data
        } catch (error) {
          console.error('请求失败:', parentId, error)
          throw error
        } finally {
          // ========== 修复4：延迟删除，避免竞态 ==========
          setTimeout(() => {
            this.pendingRequests.delete(parentId)
          }, 100)
        }
      })()
      // 记录请求
      this.pendingRequests.set(parentId, request)
      return request
    },
    /**
     * 创建节点对象
     * @param {Object} item - 原始数据
     * @param {number} level - 当前层级
     * @param {boolean} isLeaf - 是否为叶子节点
     */
    createNode(item, level, isLeaf = false) {
      return {
        ...item,
        level,
        leaf: isLeaf,
        children: isLeaf ? undefined : []
      }
    },

    /**
     * ========== 核心：自定义搜索方法 ==========
     * 只搜索最后一层（叶子节点）
     */
    filterMethod(node, keyword) {
      const nodeLabel = node.name || node.label || ''
      const isLeaf = this.isLeafNode(node)

      // ========== 关键：只搜索叶子节点 ==========
      if (!isLeaf) {
        return false
      }

      return nodeLabel.toLowerCase().includes(keyword.toLowerCase())
    },

    /**
     * ========== 核心：判断是否为叶子节点 ==========
     */
    isLeafNode(node) {
      // 明确标记为叶子
      if (node.leaf === true) {
        return true
      }

      // 如果有 children 且长度大于 0，则不是叶子
      if (node.children && node.children.length > 0) {
        return false
      }

      // 如果 children 为空数组，标记为叶子
      if (Array.isArray(node.children) && node.children.length === 0) {
        return true
      }

      // 如果超过最大层级，视为叶子
      if (this.maxLevel && node.level >= this.maxLevel - 1) {
        return true
      }

      // 如果已检查过且记录为叶子
      if (this.checkedLeafNodes.has(node.code)) {
        return true
      }

      // 默认返回 false，需要通过加载子数据确认
      return false
    },

    /**
     * ========== 核心：懒加载方法 ==========
     * Element UI 级联选择器触发
     */
    lazyLoad(node, resolve) {
      const { level, data } = node

      // 确定父级ID：根级为 '0'，其他级别使用数据的 id
      const parentId = level === 0 ? this.rootParentId : data.id


      this.fetchChildren(parentId)
          .then(children => {
            // 根据是否有子数据决定是否为叶子节点
            const hasChildren = children && children.length > 0

            // 如果没有子数据，当前节点设为叶子节点
            if (!hasChildren) {
              this.$set(data, 'leaf', true)
              this.checkedLeafNodes.add(data.code)
            }

            // 转换数据
            const nodes = (children || []).map(item => {
              // 判断当前节点是否为叶子（没有子数据）
              const isLeaf = !children || children.length === 0
              return this.createNode(item, level + 1, false)
            })

            // 如果没有子数据，子节点数组设为 undefined
            if (!hasChildren) {
              // 所有子节点都标记为叶子（虽然这里通常不会执行到这里）
            }

            // 回显处理：检查当前路径
            if (this.isEchoing && this.echoPath.length > level) {
              this.tryEchoNode(nodes, level + 1)
            }

            resolve(nodes)
          })
          .catch(error => {
            resolve([])
          })
    },

    // ========== 核心：展开事件处理（解决点击一次选中问题）==========

    /**
     * ========== 核心：展开变化事件 ==========
     * 当用户点击节点时触发
     * 关键：主动检查并预加载子数据，让叶子节点可以直接选中
     */
    async handleExpandChange(value) {

      // 如果是空数组或只有一个值（点击的是叶子节点），不处理
      if (!value || value.length === 0) return

      const lastCode = value[value.length - 1]

      // 尝试找到当前点击的节点
      let node = this.findNodeByCode(this.options, lastCode)

      if (!node) {
        // 如果在已加载的节点中找不到，尝试从路径中获取
        node = this.findNodeFromPath(value)
      }

      if (node) {

        // 如果已经是叶子节点，不需要处理
        if (node.leaf === true) {
          return
        }

        // 如果已有 children 且有数据，不是叶子节点
        if (node.children && node.children.length > 0) {
          return
        }

        // ========== 关键：主动检查是否为叶子节点 ==========
        await this.checkAndMarkLeafNode(node)
      }
    },

    /**
     * ========== 核心：检查并标记叶子节点 ==========
     * 加载子数据，如果无子数据则标记为叶子节点
     */
    async checkAndMarkLeafNode(node) {
      // 如果节点已经标记为叶子，跳过
      if (node.leaf === true) {
        return
      }

      // 如果 children 已存在且有数据，不是叶子
      if (node.children && node.children.length > 0) {
        return
      }

      try {

        // 加载子数据
        const children = await this.fetchChildren(node.id)

        if (!children || children.length === 0) {
          // ========== 关键：无子数据，标记为叶子 ==========
          this.$set(node, 'leaf', true)
          this.$set(node, 'children', undefined)
          this.checkedLeafNodes.add(node.code)


          // ========== 关键：触发一次点击让它被选中 ==========
          // 由于 Element UI 的机制，需要用户再点一次
          // 但我们已经标记为叶子，下次点击就会选中
        } else {
          // 有子数据，设置 children
          const childNodes = children.map(item => this.createNode(item, (node.level || 0) + 1))
          this.$set(node, 'children', childNodes)
        }
      } catch (error) {
      }
    },

    /**
     * 从路径中查找节点
     */
    findNodeFromPath(path) {
      if (!path || path.length === 0) return null

      let currentOptions = this.options
      let targetNode = null

      for (let i = 0; i < path.length; i++) {
        const code = path[i]
        targetNode = currentOptions.find(opt => opt.code === code)

        if (!targetNode) {
          return null
        }

        if (i === path.length - 1) {
          return targetNode
        }

        // 继续在子节点中查找
        if (targetNode.children && targetNode.children.length > 0) {
          currentOptions = targetNode.children
        } else {
          return targetNode
        }
      }

      return targetNode
    },

    // ========== 回显方法 ==========

    /**
     * 回显值
     * @param {Array|string} value - 要回显的值
     */
    async echoValue(value) {
      const path = this.parsePath(value)

      if (!path || path.length === 0) {
        return
      }


      this.isEchoing = true
      this.echoPath = path
      this.currentValue = path

      try {
        // 从第一级开始构建路径
        await this.buildEchoPath(path, this.options, 0)
      } catch (error) {
      } finally {
        this.isEchoing = false
        this.echoPath = []
      }
    },

    /**
     * 构建回显路径
     * @param {Array} path - 完整路径
     * @param {Array} options - 当前层级选项
     * @param {number} level - 当前层级
     */
    async buildEchoPath(path, options, level) {
      if (level >= path.length) return

      const targetCode = path[level]

      // 尝试在当前选项中查找
      let targetNode = this.findNodeByCode(options, targetCode)

      if (!targetNode) {
        // 未找到，加载该层级数据
        const parentId = level === 0 ? this.rootParentId : path[level - 1]
        const children = await this.fetchChildren(parentId)

        if (children && children.length > 0) {
          // 添加到选项中
          const nodes = children.map(item => this.createNode(item, level, false))
          this.addNodesToOptions(options, parentId, nodes)

          // 再次查找
          targetNode = this.findNodeByCode(options, targetCode)
        }
      }

      if (targetNode) {
        // 如果不是最后一级，继续加载
        if (level < path.length - 1) {
          // 触发懒加载获取子级
          if (!targetNode.children || targetNode.children.length === 0) {
            const children = await this.fetchChildren(targetNode.id)

            if (children && children.length > 0) {
              const nodes = children.map(item => this.createNode(item, level + 1, false))
              this.$set(targetNode, 'children', nodes)
            } else {
              // ========== 关键：没有子级，设为叶子 ==========
              this.$set(targetNode, 'leaf', true)
              this.checkedLeafNodes.add(targetNode.code)
              return
            }
          }

          // 继续下一层级
          await this.buildEchoPath(path, targetNode.children, level + 1)
        } else {
          // ========== 关键：最后一级设为叶子 ==========
          this.$set(targetNode, 'leaf', true)
          this.checkedLeafNodes.add(targetNode.code)
        }
      } else {
      }
    },

    /**
     * 在懒加载回调中尝试回显
     * @param {Array} nodes - 加载的节点
     * @param {number} level - 当前层级
     */
    tryEchoNode(nodes, level) {
      if (level >= this.echoPath.length) return

      const targetCode = this.echoPath[level]
      const targetNode = nodes.find(n => n.code === targetCode)

      if (targetNode) {
        // 预加载下一级
        this.preloadNextLevel(targetNode, level)
      }
    },

    /**
     * 预加载下一层级
     * @param {Object} node - 节点
     * @param {number} level - 当前层级
     */
    async preloadNextLevel(node, level) {
      if (level >= this.echoPath.length - 1) return

      try {
        const children = await this.fetchChildren(node.id)

        if (children && children.length > 0) {
          const nodes = children.map(item => this.createNode(item, level + 1, false))
          this.$set(node, 'children', nodes)

          // 继续尝试加载更深层级
          const nextTargetCode = this.echoPath[level + 1]
          const nextNode = nodes.find(n => n.code === nextTargetCode)

          if (nextNode) {
            await this.preloadNextLevel(nextNode, level + 1)
          }
        } else {
          // 没有子级，设为叶子
          this.$set(node, 'leaf', true)
          this.checkedLeafNodes.add(node.code)
        }
      } catch (error) {
      }
    },

    // ========== 工具方法 ==========

    /**
     * 根据 code 查找节点
     */
    findNodeByCode(options, code) {
      for (const item of options) {
        if (item.code === code) return item
        if (item.children && item.children.length > 0) {
          const found = this.findNodeByCode(item.children, code)
          if (found) return found
        }
      }
      return null
    },

    /**
     * 根据 id 查找节点
     */
    findNodeById(options, id) {
      for (const item of options) {
        if (item.id === id) return item
        if (item.children && item.children.length > 0) {
          const found = this.findNodeById(item.children, id)
          if (found) return found
        }
      }
      return null
    },

    /**
     * 添加节点到选项中
     */
    addNodesToOptions(options, parentId, nodes) {
      if (parentId === this.rootParentId) {
        nodes.forEach(node => {
          if (!this.findNodeByCode(options, node.code)) {
            options.push(node)
          }
        })
        return
      }

      const findAndAdd = (items) => {
        for (const item of items) {
          if (item.id === parentId) {
            nodes.forEach(node => {
              if (!item.children.find(c => c.code === node.code)) {
                item.children.push(node)
              }
            })
            return true
          }
          if (item.children && findAndAdd(item.children)) {
            return true
          }
        }
        return false
      }

      findAndAdd(options)
    },

    /**
     * 获取所有叶子节点（递归）
     */
    getAllLeafNodes(options = this.options, result = []) {
      for (const item of options) {
        if (this.isLeafNode(item)) {
          result.push(item)
        } else if (item.children && item.children.length > 0) {
          this.getAllLeafNodes(item.children, result)
        }
      }
      return result
    },

    // ========== 事件处理 ==========

    handleChange(value) {
      this.$emit('input', value)
      this.$emit('change', value)
    },

    // ========== 公开方法 ==========

    /**
     * 手动回显
     */
    echo(value) {
      this.echoValue(value)
    },

    /**
     * 清除选中
     */
    clear() {
      this.currentValue = []
      this.$emit('input', [])
      this.$emit('change', [])
    },

    /**
     * 刷新数据
     */
    refresh() {
      this.dataCache.clear()
      this.checkedLeafNodes.clear()
      this.options = []
      this.loadRootData()
    },

    /**
     * 获取选中节点
     */
    getCheckedNodes() {
      return this.$refs.cascaderRef?.getCheckedNodes() || []
    },

    /**
     * 获取选中节点的信息
     */
    getCheckedNodeInfo() {
      const nodes = this.getCheckedNodes()
      if (nodes.length > 0) {
        return {
          code: nodes[0].value,
          name: nodes[0].label,
          data: nodes[0].data
        }
      }
      return null
    },

    /**
     * 获取组件实例
     */
    getInstance() {
      return this.$refs.cascaderRef
    },

    /**
     * 手动搜索（支持异步搜索）
     */
    async search(keyword) {
      if (!keyword) {
        this.searchResults = []
        return []
      }

      this.isSearching = true

      try {
        const keywordLower = keyword.toLowerCase()

        // 1. 收集所有已加载的叶子节点
        const allLeaves = this.getAllLeafNodes()

        // 2. 过滤匹配的节点
        const matched = allLeaves.filter(node => {
          const name = (node.name || '').toLowerCase()
          return name.includes(keywordLower)
        })

        this.searchResults = matched.map(node => ({
          code: node.code,
          name: node.name,
          path: this.buildNodePath(node)
        }))

        return this.searchResults
      } finally {
        this.isSearching = false
      }
    },

    /**
     * 构建节点的完整路径
     */
    buildNodePath(node) {
      const path = [node.code]
      let current = node

      // 向上遍历构建路径
      while (current.parent) {
        path.unshift(current.parent.code)
        current = current.parent
      }

      return path
    }
  }
}
</script>

<style scoped>
/* 可根据需要添加自定义样式 */
</style>
